import type { FormatMessage } from '@aurora/shared-types/texts';
import type { GroupValues } from '../../components/form/GroupedDropDownSelectField/GroupedDropDownSelectField';
import type {
  IdeaStatusDetails,
  IdeaStatusGroup
} from '@aurora/shared-generated/types/graphql-schema-types';

/**
 * Function to transform the status groups into grouped dropdown data format.
 * @param statusGroups - the idea status groups
 * @param defaultStatus - the idea default status
 * @param formatMessage - the format message
 */
export function getTransformStatusGroups(
  statusGroups: IdeaStatusGroup[],
  defaultStatus: string,
  formatMessage: FormatMessage
): GroupValues[] {
  return statusGroups
    .filter(statusGroup => statusGroup.statuses.length > 0)
    .map(group => ({
      groupKey: group.groupKey.split('_').join(' '),
      groupItems: group.statuses.map(statusItem => ({
        key: statusItem.statusKey,
        value:
          statusItem.statusKey == defaultStatus
            ? formatMessage('defaultStatusValue', { defaultStatus: statusItem.text })
            : statusItem.text
      }))
    }));
}

/**
 * Take in a list of status keys together with the idea status groups tree
 * and return a list of the selected statuses with their properties
 * @param statusGroups the idea status groups tree
 * @param statusKeys list of status keys to extract statuses for
 * @returns Array of the selected statuses with all their properties
 */
export function extractStatusesFromGroupTree(
  statusGroups: IdeaStatusGroup[],
  statusKeys: string[],
  comparator: 'includes' | 'excludes'
): IdeaStatusDetails[] {
  let ideaStatusList: IdeaStatusDetails[] = [];
  for (const group of statusGroups) {
    ideaStatusList = [
      ...ideaStatusList,
      ...group.statuses.filter(ideaStatus =>
        statusKeys.includes(ideaStatus.statusKey)
          ? comparator === 'includes'
          : comparator === 'excludes'
      )
    ];
  }
  return ideaStatusList;
}

/**
 * The unspecified status key.
 */
export const unspecifiedStatus = 'unspecified';
